/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { AllegatoRichiestaDTO } from '../schemas/allegatoRichiestaDTO.ts';
import type { CartelleProcedimentoAreaPubblicaDto } from '../schemas/cartelleProcedimentoAreaPubblicaDto.ts';
import type { GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceParams } from '../schemas/getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceParams.ts';
import type { GetFrontAreaPubblicaRichiesteParams } from '../schemas/getFrontAreaPubblicaRichiesteParams.ts';
import type { RichiestaModuloDTO } from '../schemas/richiestaModuloDTO.ts';
import type { RichiestaPubblicaDto } from '../schemas/richiestaPubblicaDto.ts';
import type { WsRispostaPaginataListRichiestaPubblicaDto } from '../schemas/wsRispostaPaginataListRichiestaPubblicaDto.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce l'elenco richieste pubbliche che sono attuali in base alla data di inizio e fine pubblicazione
 * @summary Elenco richieste pubbliche
 */
export const getFrontAreaPubblicaRichieste = (
  params?: GetFrontAreaPubblicaRichiesteParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListRichiestaPubblicaDto>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteQueryKey = (
  params?: GetFrontAreaPubblicaRichiesteParams,
) =>
  [
    `/framework-cmp-domande/area-pubblica/richieste`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontAreaPubblicaRichiesteQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontAreaPubblicaRichiesteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontAreaPubblicaRichiesteQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichieste(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontAreaPubblicaRichiesteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>
>;
export type GetFrontAreaPubblicaRichiesteQueryError = ErrorType<unknown>;

/**
 * @summary Elenco richieste pubbliche
 */
export const useGetFrontAreaPubblicaRichieste = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontAreaPubblicaRichiesteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichieste>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Dettaglio della richiesta pubblica
 * @summary Dettaglio richiesta pubblica
 */
export const getFrontAreaPubblicaRichiesteId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaPubblicaDto>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/area-pubblica/richieste/${id}`] as const;

export const getGetFrontAreaPubblicaRichiesteIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontAreaPubblicaRichiesteIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontAreaPubblicaRichiesteIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>
>;
export type GetFrontAreaPubblicaRichiesteIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio richiesta pubblica
 */
export const useGetFrontAreaPubblicaRichiesteId = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Scarica un file di una comunicazione, gruppo allegato o documento (atto)
 */
export const getFrontAreaPubblicaRichiesteIdTipoFileId = (
  id: string,
  tipoFile: string,
  idFile: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/${tipoFile}/${idFile}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdTipoFileIdQueryKey = (
  id: string,
  tipoFile: string,
  idFile: string,
) =>
  [
    `/framework-cmp-domande/area-pubblica/richieste/${id}/${tipoFile}/${idFile}`,
  ] as const;

export const getGetFrontAreaPubblicaRichiesteIdTipoFileIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  tipoFile: string,
  idFile: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontAreaPubblicaRichiesteIdTipoFileIdQueryKey(id, tipoFile, idFile);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteIdTipoFileId(
      id,
      tipoFile,
      idFile,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(id && tipoFile && idFile),
    ...queryOptions,
  };
};

export type GetFrontAreaPubblicaRichiesteIdTipoFileIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>
>;
export type GetFrontAreaPubblicaRichiesteIdTipoFileIdQueryError =
  ErrorType<unknown>;

/**
 * @summary Scarica un file di una comunicazione, gruppo allegato o documento (atto)
 */
export const useGetFrontAreaPubblicaRichiesteIdTipoFileId = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  tipoFile: string,
  idFile: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdTipoFileId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdTipoFileIdQueryOptions(
    id,
    tipoFile,
    idFile,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Moduli associati ad una richiesta pubblica
 * @summary Moduli di una richiesta pubblica
 */
export const getFrontAreaPubblicaRichiesteIdModuli = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaModuloDTO[]>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/moduli`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdModuliQueryKey = (id: string) =>
  [`/framework-cmp-domande/area-pubblica/richieste/${id}/moduli`] as const;

export const getGetFrontAreaPubblicaRichiesteIdModuliQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontAreaPubblicaRichiesteIdModuliQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteIdModuli(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontAreaPubblicaRichiesteIdModuliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>
>;
export type GetFrontAreaPubblicaRichiesteIdModuliQueryError =
  ErrorType<unknown>;

/**
 * @summary Moduli di una richiesta pubblica
 */
export const useGetFrontAreaPubblicaRichiesteIdModuli = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdModuliQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Dettaglio modulo di una richiesta pubblica
 * @summary Dettaglio modulo di una richiesta pubblica
 */
export const getFrontAreaPubblicaRichiesteIdModuliId = (
  id: string,
  idRichiestaModulo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaModuloDTO>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/moduli/${idRichiestaModulo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdModuliIdQueryKey = (
  id: string,
  idRichiestaModulo: string,
) =>
  [
    `/framework-cmp-domande/area-pubblica/richieste/${id}/moduli/${idRichiestaModulo}`,
  ] as const;

export const getGetFrontAreaPubblicaRichiesteIdModuliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontAreaPubblicaRichiesteIdModuliIdQueryKey(id, idRichiestaModulo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteIdModuliId(
      id,
      idRichiestaModulo,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(id && idRichiestaModulo),
    ...queryOptions,
  };
};

export type GetFrontAreaPubblicaRichiesteIdModuliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>
>;
export type GetFrontAreaPubblicaRichiesteIdModuliIdQueryError =
  ErrorType<unknown>;

/**
 * @summary Dettaglio modulo di una richiesta pubblica
 */
export const useGetFrontAreaPubblicaRichiesteIdModuliId = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdModuliIdQueryOptions(
    id,
    idRichiestaModulo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Scarica un allegato della richiesta
 */
export const getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice = (
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/moduli/${idRichiestaModulo}/sezioni/${idSezione}/file/${codice}`,
      method: 'get',
      params,
      responseType: 'blob',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceQueryKey =
  (
    id: string,
    idRichiestaModulo: string,
    idSezione: string,
    codice: string,
    params?: GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceParams,
  ) =>
    [
      `/framework-cmp-domande/area-pubblica/richieste/${id}/moduli/${idRichiestaModulo}/sezioni/${idSezione}/file/${codice}`,
      ...(params ? [params] : []),
    ] as const;

export const getGetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
      >
    >,
    TError = ErrorType<unknown>,
  >(
    id: string,
    idRichiestaModulo: string,
    idSezione: string,
    codice: string,
    params?: GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceParams,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceQueryKey(
        id,
        idRichiestaModulo,
        idSezione,
        codice,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
        >
      >
    > = ({ signal }) =>
      getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice(
        id,
        idRichiestaModulo,
        idSezione,
        codice,
        params,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(id && idRichiestaModulo && idSezione && codice),
      ...queryOptions,
    };
  };

export type GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
      >
    >
  >;
export type GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceQueryError =
  ErrorType<unknown>;

/**
 * @summary Scarica un allegato della richiesta
 */
export const useGetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice = <
  TData = Awaited<
    ReturnType<
      typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
    >
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: GetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodice
        >
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontAreaPubblicaRichiesteIdModuliIdSezioniIdFileCodiceQueryOptions(
      id,
      idRichiestaModulo,
      idSezione,
      codice,
      params,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * PDF della richiesta pubblica
 * @summary Scarica il pdf della richiesta pubblica
 */
export const getFrontAreaPubblicaRichiesteIdDownload = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/download`,
      method: 'get',
      responseType: 'blob',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdDownloadQueryKey = (
  id: string,
) => [`/framework-cmp-domande/area-pubblica/richieste/${id}/download`] as const;

export const getGetFrontAreaPubblicaRichiesteIdDownloadQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontAreaPubblicaRichiesteIdDownloadQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteIdDownload(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontAreaPubblicaRichiesteIdDownloadQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>
>;
export type GetFrontAreaPubblicaRichiesteIdDownloadQueryError =
  ErrorType<unknown>;

/**
 * @summary Scarica il pdf della richiesta pubblica
 */
export const useGetFrontAreaPubblicaRichiesteIdDownload = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdDownloadQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontAreaPubblicaRichiesteIdCartelle = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CartelleProcedimentoAreaPubblicaDto>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/cartelle`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdCartelleQueryKey = (
  id: string,
) => [`/framework-cmp-domande/area-pubblica/richieste/${id}/cartelle`] as const;

export const getGetFrontAreaPubblicaRichiesteIdCartelleQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontAreaPubblicaRichiesteIdCartelleQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteIdCartelle(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontAreaPubblicaRichiesteIdCartelleQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>
>;
export type GetFrontAreaPubblicaRichiesteIdCartelleQueryError =
  ErrorType<unknown>;

export const useGetFrontAreaPubblicaRichiesteIdCartelle = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdCartelle>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdCartelleQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupera i dettagli sugli allegati di una richiesta
 * @summary Recupera i dettagli sugli allegati di una richiesta
 */
export const getFrontAreaPubblicaRichiesteIdAllegati = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AllegatoRichiestaDTO[]>(
    {
      url: `/framework-cmp-domande/area-pubblica/richieste/${id}/allegati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontAreaPubblicaRichiesteIdAllegatiQueryKey = (
  id: string,
) => [`/framework-cmp-domande/area-pubblica/richieste/${id}/allegati`] as const;

export const getGetFrontAreaPubblicaRichiesteIdAllegatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontAreaPubblicaRichiesteIdAllegatiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>
  > = ({ signal }) =>
    getFrontAreaPubblicaRichiesteIdAllegati(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontAreaPubblicaRichiesteIdAllegatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>
>;
export type GetFrontAreaPubblicaRichiesteIdAllegatiQueryError =
  ErrorType<unknown>;

/**
 * @summary Recupera i dettagli sugli allegati di una richiesta
 */
export const useGetFrontAreaPubblicaRichiesteIdAllegati = <
  TData = Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontAreaPubblicaRichiesteIdAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontAreaPubblicaRichiesteIdAllegatiQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
